import { Checkbox, Form, Input, InputNumber, Switch } from 'antd';

import { apiFetch } from '@/api/client';
import { type VendorCapability } from '@/api/generated/enums';
import { vendorsKey, type Vendor } from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { FormDrawer } from '@/components/FormDrawer';
import { Notice } from '@/components/PageHeading';

import { capabilityHints, capabilityLabels, selectableCapabilities } from './capabilities';

/** `capabilities`: what a new vendor starts ticked — Supply, when a new order adds one. */
export type Editing =
  { mode: 'create'; capabilities?: VendorCapability[] } | { mode: 'edit'; vendor: Vendor };

export interface VendorFormValues {
  code: string;
  name: string;
  capabilities: VendorCapability[];
  mobile: string;
  /** Create only — scope is fixed once the row exists, as on every other master. */
  shared?: boolean;
  contactPerson?: string | null;
  alternateMobile?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateCode?: string | null;
  stateName?: string | null;
  pincode?: string | null;
  gstin?: string | null;
  isMsme?: boolean;
  udyamNumber?: string | null;
  paymentTermDays?: number | null;
  standardTurnaroundDays?: number | null;
  notes?: string | null;
  isActive?: boolean;
}

/**
 * Create or correct a vendor (spec Sections 10, 11 and 16).
 *
 * <p>
 * One form for all three of the spec's vendor tables. What decides which lists a vendor appears in
 * is the tick boxes at the top, not which screen created them — so the dry cleaner who starts taking
 * in alterations is one tick, rather than a second row somebody has to keep in step with the first.
 * </p>
 *
 * <p>
 * <strong>The code is create-only</strong>, like every other master's, and the reason is sharper
 * here: Section 30.1's Tally export keys the purchase ledger on it, so an edit would silently
 * re-point every bill already exported under the old one. The mobile number is editable, exactly as
 * the customer's is.
 * </p>
 */
export function VendorDrawer({
  editing,
  onClose,
  onCreated,
}: {
  editing: Editing;
  onClose: () => void;
  /** A new vendor, saved — a new order chooses them. */
  onCreated?: ((vendor: Vendor) => void) | undefined;
}) {
  const [form] = Form.useForm<VendorFormValues>();
  const isEdit = editing.mode === 'edit';
  const existing = editing.mode === 'edit' ? editing.vendor : undefined;

  // Watched rather than read once: a Udyam number without the MSME box ticked is refused by the
  // server, so the field only appears once the box is on.
  const isMsme = Form.useWatch('isMsme', form);

  const save = useApiMutation<Vendor, VendorFormValues>({
    mutationFn: (values) =>
      isEdit
        ? apiFetch<Vendor>(`/api/vendor/${existing?.id}`, {
            method: 'PUT',
            // The update states the whole record; left out, the docket language reads as null and
            // the next docket asks the tailor's language again. It is set from a docket, not here.
            body: { ...values, docketLanguage: existing?.docketLanguage ?? null },
          })
        : apiFetch<Vendor>('/api/vendor', { method: 'POST', body: values }),
    success: isEdit ? 'Vendor updated.' : 'Vendor created.',
    invalidates: [vendorsKey],
    onSuccess: (saved) => {
      if (!isEdit) {
        onCreated?.(saved);
      }

      onClose();
    },
  });

  return (
    <FormDrawer<VendorFormValues>
      title={isEdit ? `Edit ${existing?.name ?? 'vendor'}` : 'New vendor'}
      open
      onClose={onClose}
      form={form}
      initialValues={
        existing ?? {
          code: '',
          name: '',
          capabilities: editing.mode === 'create' ? (editing.capabilities ?? []) : [],
          mobile: '',
          shared: false,
          isActive: true,
        }
      }
      onSubmit={(values) => void submitVia(save, values)}
      submitText={isEdit ? 'Save' : 'Create'}
      submitting={save.isPending}
      width={560}
    >
      {!isEdit && (
        <Form.Item
          name="code"
          label="Code"
          extra="Fixed once created — it is what a purchase order and the Tally export carry."
          rules={[{ required: true, message: 'A vendor code is required.' }]}
        >
          <Input placeholder="SHREE-DC" />
        </Form.Item>
      )}

      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: 'A name is required.' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="capabilities"
        label="What they do for you"
        extra="Tick everything that applies. One vendor who both cleans and alters is one row here, not two."
        rules={[{ required: true, message: 'Tick at least one.' }]}
      >
        <Checkbox.Group className="flex flex-col gap-2">
          {selectableCapabilities.map((capability) => (
            <Checkbox key={capability} value={capability}>
              <span className="font-medium">{capabilityLabels[capability]}</span>
              <span className="ms-2 text-[12px] text-default">{capabilityHints[capability]}</span>
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>

      <Form.Item
        name="mobile"
        label="Mobile"
        extra="Unique across the whole company — if this number is already on file, that vendor is this one."
        rules={[{ required: true, message: 'A mobile number is required.' }]}
      >
        <Input placeholder="98241 01010" inputMode="tel" />
      </Form.Item>

      <Form.Item name="contactPerson" label="Who to ask for">
        <Input placeholder="Jayesh Bhai" />
      </Form.Item>

      <Form.Item name="alternateMobile" label="Alternate number">
        <Input placeholder="The workshop line" inputMode="tel" />
      </Form.Item>

      <Form.Item name="email" label="Email">
        <Input type="email" />
      </Form.Item>

      {!isEdit && (
        <Form.Item
          name="shared"
          label="Shared across branches"
          valuePropName="checked"
          extra="Recommended for a chain — most shops use one launderer company-wide. Needs a company-wide role."
        >
          <Switch />
        </Form.Item>
      )}

      <Form.Item
        name="standardTurnaroundDays"
        label="Turnaround (days)"
        extra="What they promise: days from handing work over to getting it back. Leave blank if you have not agreed one."
      >
        <InputNumber
          min={0}
          max={365}
          className="w-full"
          onWheel={(event) => event.currentTarget.blur()}
        />
      </Form.Item>

      <Form.Item name="addressLine1" label="Address">
        <Input placeholder="Line 1" />
      </Form.Item>

      <Form.Item name="addressLine2" label=" " colon={false}>
        <Input placeholder="Line 2" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item name="city" label="City">
          <Input />
        </Form.Item>

        <Form.Item name="pincode" label="Pincode">
          <Input inputMode="numeric" />
        </Form.Item>

        <Form.Item name="stateName" label="State">
          <Input />
        </Form.Item>

        <Form.Item
          name="stateCode"
          label="State code"
          // Place of supply on a purchase bill is what decides CGST+SGST against IGST, and
          // therefore what credit is claimable.
          extra="GST place of supply — 24 for Gujarat."
        >
          <Input maxLength={2} inputMode="numeric" />
        </Form.Item>
      </div>

      <Form.Item
        name="gstin"
        label="GSTIN"
        extra="Blank means unregistered — their bills carry no claimable credit."
      >
        <Input maxLength={15} />
      </Form.Item>

      <Form.Item
        name="paymentTermDays"
        label="Payment terms (days)"
        extra="Blank means payable on presentation. Zero is a term you agreed; blank is one you did not."
      >
        <InputNumber
          min={0}
          max={365}
          className="w-full"
          onWheel={(event) => event.currentTarget.blur()}
        />
      </Form.Item>

      <Form.Item
        name="isMsme"
        label="Registered MSME"
        valuePropName="checked"
        extra="Their invoices carry a statutory 45-day payment window."
      >
        <Switch />
      </Form.Item>

      {isMsme && (
        <Form.Item name="udyamNumber" label="Udyam number">
          <Input placeholder="UDYAM-GJ-11-0004567" />
        </Form.Item>
      )}

      <Form.Item name="notes" label="Notes">
        <Input.TextArea rows={2} />
      </Form.Item>

      {isEdit && (
        <Form.Item name="isActive" label="Still used" valuePropName="checked">
          <Switch />
        </Form.Item>
      )}

      <Notice tone="info" title="Jobs and rates come later">
        Cleaning jobs, alteration jobs, their challans and this vendor&apos;s rate card are their
        own screens, and are not built yet. What is here is who they are and how to reach them.
      </Notice>
    </FormDrawer>
  );
}
